<template>
    <div>
        <form @submit.prevent="importFile">
            <div class="form-group">
                <label>Upload File CSV:</label>
                <input ref="fileInput" type="file" @change="handleFileChange" accept=".csv" class="form-control" />
                <small class="form-text text-muted">
                    Format CSV harus berisi kolom: <code>name</code>, <code>number</code>, dan <code>birth_date</code> (boleh juga <code>tgl lahir</code> / <code>tanggal_lahir</code>) dengan format tanggal <code>YYYY-MM-DD</code>, <code>DD-MM-YYYY</code>, atau <code>DD/MM/YYYY</code>.
                </small>
                <div class="mt-2">
                    <a href="/excel/contohbirthday.csv" download class="btn btn-sm btn-outline-secondary">
                        <i class="fas fa-download"></i> Unduh Contoh CSV
                    </a>
                </div>
            </div>
            <div class="mt-2" v-if="csvParseLoading">
                <div class="alert alert-warning mb-0">
                    Sedang memproses file CSV...
                </div>
            </div>
            <div class="form-group mt-3">
                <label>
                    <input type="checkbox" v-model="updateExisting" :value="1" :unchecked-value="0" checked>
                    Update data kontak jika sudah ada
                </label>
            </div>
            <button type="submit" class="btn btn-primary" :disabled="!csvImportReady || csvParseLoading">Import</button>
        </form>
        <div v-if="csvImportReady && parsedRows.length && !importResult" class="mt-4">
            <h5 class="mb-3 fw-600 text-capitalize text-center">Preview Data</h5>
            <vue-good-table
                :columns="previewColumns"
                :rows="parsedRows"
                :pagination-options="{
                    enabled: true,
                    perPage: 5,
                    perPageDropdown: [5, 10]
                }"
            />
        </div>
        <div v-if="importResult" class="mt-3">
            <div class="alert alert-info">
                <strong>Hasil Import:</strong><br>
                Berhasil: {{ importResult.success_count }}<br>
                Gagal: {{ importResult.error_count }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            selectedFile: null,
            updateExisting: 1,
            importResult: null,
            parsedRows: [],
            csvParseLoading: false,
            csvImportReady: false,
            previewColumns: [
                { label: 'Nama', field: 'name' },
                { label: 'Nomor', field: 'number' },
                {
                    label: 'Tanggal Lahir',
                    field: 'birth_date',
                    formatFn: (value) => {
                        if (!value) return '-';
                        const date = new Date(value);
                        if (isNaN(date.getTime())) {
                            return value;
                        }
                        return date.toLocaleDateString('id-ID');
                    }
                }
            ]
        };
    },
    methods: {
        handleFileChange(event) {
            this.selectedFile = event.target.files[0];
            this.importResult = null;
            this.parsedRows = [];
            this.csvImportReady = false;

            if (!this.selectedFile) {
                return;
            }

            const isCsv = this.selectedFile.type === "text/csv" || this.selectedFile.name.toLowerCase().endsWith(".csv");
            if (!isCsv) {
                this.$toast.error("File harus berformat .csv");
                this.selectedFile = null;
                return;
            }

            this.parseCsvPreview(this.selectedFile);
        },
        async importFile() {
            if (this.csvParseLoading) {
                this.$toast.error("Sedang memproses file CSV");
                return;
            }
            if (!this.parsedRows.length) {
                this.$toast.error("Tidak ada data valid untuk diimport");
                return;
            }
            try {
                const response = await this.$store.dispatch("birthday/birthday_import", {
                    update_existing: this.updateExisting == 1 ? true : false,
                    contacts: this.parsedRows
                });
                this.importResult = response.data.data;
                this.$toast.success(`Import selesai: ${response.data.data.success_count} berhasil, ${response.data.data.error_count} gagal`);
                this.resetForm();
            } catch (err) {
                const message = err.response?.data?.message || "Gagal import data";
                this.$toast.error("Gagal import data: " + message);
            }
        },
        resetForm() {
            this.selectedFile = null;
            this.parsedRows = [];
            this.csvImportReady = false;
            if (this.$refs && this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        },
        parseCsvPreview(file) {
            this.csvParseLoading = true;
            this.$papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    const rows = results.data || [];
                    this.parsedRows = rows
                        .map(row => this.normalizeRow(row))
                        .filter(row => row.name && row.number && row.birth_date);
                    this.csvImportReady = this.parsedRows.length > 0;
                    if (!this.csvImportReady) {
                        this.$toast.error("Tidak ada baris valid pada file CSV");
                    }
                    this.csvParseLoading = false;
                },
                error: () => {
                    this.$toast.error("Gagal membaca file CSV");
                    this.csvParseLoading = false;
                }
            });
        },
        normalizeRow(raw) {
            const row = {};
            Object.keys(raw).forEach(key => {
                if (!key) return;
                row[key.toLowerCase().trim()] = raw[key];
            });

            const name = (row['name'] || row['nama'] || '').toString().trim();
            const number = (row['number'] || row['nomor'] || row['phone'] || '').toString().trim();
            const birthRaw = row['birth_date'] || row['birthdate'] || row['tanggal_lahir'] || row['tgl lahir'] || row['tgl_lahir'] || row['date'];

            let birthDate = null;
            if (birthRaw) {
                birthDate = this.normalizeBirthDate(birthRaw);
            }

            return {
                name,
                number,
                birth_date: birthDate
            };
        },
        normalizeBirthDate(value) {
            if (value === undefined || value === null) return null;
            const str = value.toString().trim();
            if (!str) return null;

            if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
                return str;
            }

            const parts = str.split(/[-/]/);
            if (parts.length === 3) {
                let [day, month, year] = parts.map(part => part.toString().trim());
                if (year.length === 2) {
                    year = year < "50" ? `20${year}` : `19${year}`;
                }
                const iso = `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
                if (!isNaN(Date.parse(iso))) {
                    return iso;
                }
            }

            const parsed = new Date(str);
            if (!isNaN(parsed.getTime())) {
                const year = parsed.getFullYear();
                const month = String(parsed.getMonth() + 1).padStart(2, '0');
                const day = String(parsed.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            return null;
        }
    }
};
</script>

